import { AdvancedDailyEngagedUserUsage } from 'models/billing/advancedDailyEngagedUser/AdvancedDailyEngagedUserUsage';
import { METHOD, RequestStatus } from 'shared/Requests';
import { useSubscriptionReferenceDate } from './BillingApi';
import { BlipApi } from 'api/BlipApi';
import { AdvancedDailyEngagedUserUsageTotal } from 'models/billing/advancedDailyEngagedUser/AdvancedDailyEngagedUserUsageTotal';
import { AdvancedDailyEngagedUserMonthlyUsage } from 'models/billing/advancedDailyEngagedUser/AdvancedDailyEngagedUserMonthlyUsage';

export default class BillingAdvancedDailyEngagedUserServiceApi {
  private readonly BILLING_COMMAND_DESTINATION = 'postmaster@billing.blip.ai';
  private code = '';
  private status = '';
  private useSubscriptionReferenceDate: boolean | undefined = undefined;

  public async getUseSubscriptionReferenceDate(): Promise<void> {
    if (this.useSubscriptionReferenceDate === undefined) {
      this.useSubscriptionReferenceDate = await useSubscriptionReferenceDate();
    }
  }

  public getStatus(): string {
    return this.status;
  }

  public getCode(): string {
    return this.code;
  }

  public setSuccess(): void {
    this.status = RequestStatus.SUCCESS;
  }

  public setErrorLog(e: unknown): void {
    const error = JSON.parse(<string>e);
    const {
      status,
      reason: { code },
    } = JSON.parse(error.message);
    this.code = code;
    this.status = status;
  }

  public async getTotalAdvancedDailyEngagedUserConsumption(
    tenantId: string,
    month: string,
    metricIds: string,
    storageType = 1,
  ): Promise<AdvancedDailyEngagedUserUsageTotal | null> {
    await this.getUseSubscriptionReferenceDate();

    const uri =
      `/tenants/${tenantId}/monthly-message-metrics/total?month=${month}` +
      `&metricIdList=${metricIds}` +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return new AdvancedDailyEngagedUserUsageTotal({ items });
    } catch (e) {
      this.setErrorLog(e);
      return new AdvancedDailyEngagedUserUsageTotal({ items: [] });
    }
  }

  public async getDailyAdvancedDailyEngagedUserConsumption(
    tenantId: string,
    metricIds: string,
    month: string,
    registersToTake: number,    
    storageType = 1,
  ): Promise<AdvancedDailyEngagedUserUsage[]> {
    await this.getUseSubscriptionReferenceDate();

    let uri =
      `/tenants/${tenantId}/monthly-message-metrics/daily?month=${month}` +
      `&metricIdList=${metricIds}` +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}`;

    uri = uri + `&$take=${registersToTake}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return items.map((item) => new AdvancedDailyEngagedUserUsage(item));
    } catch (e) {
      this.setErrorLog(e);
      return new Array<AdvancedDailyEngagedUserUsage>();
    }
  }

  public async getAdvancedDailyEngagedUserMonthlyUsage(
    tenantId: string,
    metricIds: string,
    month: string,
    registersToTake: number,
    storageType = 1,
  ): Promise<AdvancedDailyEngagedUserMonthlyUsage[]> {
    await this.getUseSubscriptionReferenceDate();

    let uri =
      `/tenants/${tenantId}/monthly-message-metrics/monthly/by-bot?month=${month}` +
      `&metricIdList=${metricIds}` +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}`;

    uri = uri + `&$take=${registersToTake}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return items.map((item) => new AdvancedDailyEngagedUserMonthlyUsage(item));
    } catch (e) {
      this.setErrorLog(e);
      return new Array<AdvancedDailyEngagedUserMonthlyUsage>();
    }
  }
}
